import React from "react";
import { css } from "glamor";
import { FaExclamationCircle } from "react-icons/fa";
import formatDate from "../../utils/formatDate";
import { toBRLCurrency } from "../../utils/formatCurrency";
import {
  retryDateText,
  pendingPixExpiresAt,
  overdueDaysText,
} from "./financeSubscriptionHelpers";

const overdueClass = css({
  display: "flex",
  gap: 10,
  marginBottom: 14,
  padding: 14,
  borderRadius: 8,
  border: "0.6px solid #FED7AA",
  background: "#FFF7ED",
  color: "#9A3412",
  overflowWrap: "anywhere",
  "@media(max-width: 520px)": {
    padding: 12,
  },
});

const overdueIconClass = css({
  width: 28,
  height: 28,
  minWidth: 28,
  borderRadius: "50%",
  background: "#FFEDD5",
  color: "#C2410C",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 14,
  flexShrink: 0,
});

const overdueContentClass = css({
  display: "flex",
  flexDirection: "column",
  gap: 4,
  minWidth: 0,
  flex: 1,
});

const overdueTitleClass = css({
  color: "#C2410C",
  fontSize: 12,
  fontWeight: 600,
  lineHeight: "18px",
});

const overdueDescriptionClass = css({
  color: "#9A3412",
  fontSize: 11,
  fontWeight: 400,
  lineHeight: "17px",
});

const overdueDetailsRowClass = css({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "4px 10px",
  marginTop: 2,
});

const overdueDetailClass = css({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  minWidth: 0,
});

const overdueDetailLabelClass = css({
  color: "#9A3412",
  fontSize: 10,
  fontWeight: 400,
  lineHeight: "15px",
});

const overdueDetailValueClass = css({
  color: "#C2410C",
  fontSize: 11,
  fontWeight: 600,
  lineHeight: "16px",
});

const overdueRetryButtonClass = css({
  alignSelf: "flex-start",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 31,
  marginTop: 8,
  padding: "0 14px",
  border: "none",
  borderRadius: 6,
  background: "#C2410C",
  color: "#FFFFFF",
  fontSize: 11,
  fontWeight: 600,
  lineHeight: "16px",
  cursor: "pointer",
  "@media(max-width: 420px)": {
    width: "100%",
  },
});

const overdueLimitClass = css({
  marginTop: 8,
  color: "#9A3412",
  fontSize: 11,
  fontWeight: 500,
  lineHeight: "16px",
});

export default function FailedChargeBanner({ subscription, overdueDays, chargeFailureReason, price, onRetryClick }) {
  const isPendingPix = !!subscription.pendingPixPayment;
  const nextRetryDate = retryDateText(subscription);
  const expiresAt = pendingPixExpiresAt(subscription);
  const retryCount = subscription.chargeRetryCount || 0;
  const maxRetries = subscription.maxChargeRetries || 5;
  const pendingValue = subscription.pendingPixPayment?.value || price || subscription.price;

  return (
    <div className={overdueClass}>
      <div className={overdueIconClass}>
        <FaExclamationCircle />
      </div>

      <div className={overdueContentClass}>
        <strong className={overdueTitleClass}>
          {isPendingPix ? "Pagamento pendente" : `Pagamento em atraso${overdueDaysText(overdueDays)}`}
        </strong>

        <span className={overdueDescriptionClass}>
          {isPendingPix
            ? "Finalize o pagamento via Pix para manter sua assinatura ativa."
            : chargeFailureReason}
        </span>

        <div className={overdueDetailsRowClass}>
          <span className={overdueDetailClass}>
            <span className={overdueDetailLabelClass}>Valor pendente:</span>
            <span className={overdueDetailValueClass}>{toBRLCurrency(pendingValue)}</span>
          </span>

          {isPendingPix && expiresAt && (
            <span className={overdueDetailClass}>
              <span className={overdueDetailLabelClass}>Vencimento:</span>
              <span className={overdueDetailValueClass}>{formatDate(expiresAt, "DD/MM/YYYY")}</span>
            </span>
          )}

          {!isPendingPix && nextRetryDate && (
            <span className={overdueDetailClass}>
              <span className={overdueDetailLabelClass}>Próx. tentativa:</span>
              <span className={overdueDetailValueClass}>{nextRetryDate}</span>
            </span>
          )}
        </div>

        {isPendingPix ? (
          <button type="button" className={overdueRetryButtonClass}>
            Ver pagamento pendente
          </button>
        ) : subscription.canRetryCharge ? (
          <button type="button" className={overdueRetryButtonClass} onClick={onRetryClick}>
            Tentar cobrança novamente
          </button>
        ) : (
          <span className={overdueLimitClass}>
            Limite de tentativas atingido ({retryCount}/{maxRetries}). Entre em contato com o suporte.
          </span>
        )}
      </div>
    </div>
  );
}
